import { useState, useEffect } from 'react';
import { getSessionId } from '../utils/session';
import type { ModuleId } from '../types';

interface SearchModuleProps {
  onNavigate?: (moduleId: ModuleId) => void;
}

type Stats = {
  topQueries: string[];
  topActions: string[];
  topEvents: {
    search_query: number;
    ai_response: number;
    execution_click: number;
  };
};

export default function SearchModule({ onNavigate }: SearchModuleProps = {}) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState('');
  const [aiStatus, setAiStatus] = useState('READY');

  const handleSearch = async () => {
    if (!query) return;

    setResult('Analyzing...');
    setAiStatus('ANALYZING...');

    // Track search query
    try {
      await fetch(`/api/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          eventType: 'search_query', 
          query,
          sessionId: getSessionId()
        })
      });
    } catch (e) {
      console.log('Tracking failed:', e);
    }

    try {
      const response = await fetch(`/api/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: query, module: 'search' })
      });

      const data = await response.json();

      // Track AI response
      try {
        await fetch(`/api/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            eventType: 'ai_response', 
            query, 
            response: data.output?.substring(0, 100),
            sessionId: getSessionId()
          })
        });
      } catch (e) {
        console.log('Tracking failed:', e);
      }

      // Enhance output with NWG execution paths
      let enhancedOutput = data.output;

      // Detect keywords and add NWG ecosystem connection
      if (
        query.toLowerCase().includes('passive income') || 
        query.toLowerCase().includes('money') ||
        query.toLowerCase().includes('online business') ||
        query.toLowerCase().includes('revenue') ||
        query.toLowerCase().includes('income')
      ) {
        enhancedOutput += `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NWG EXECUTION PATH:

- Recommended System: NextWealth™ Global Starter Funnel
- Action: Launch affiliate funnel (Systeme.io)
- Time to Deploy: 1–2 hours
- Entry Point: [Attach your funnel link here]

NOTE:
This keeps user inside NWG ecosystem for execution.`;
      }

      // Fetch stats (non-blocking)
      let statsData = null;
      try {
        statsData = await fetch(`/api/track/stats`)
          .then(res => res.json());
      } catch (e) {
        statsData = null;
      }

      // Build insight block
      let insightBlock = '';

      if (query.toLowerCase().includes('money') || query.toLowerCase().includes('income')) {
        insightBlock += `

SYSTEM INSIGHT:

Users searching for income strategies are most successful when launching the NextWealth™ system.`;
      }

      // Add high-conversion signal
      if (statsData && statsData.topEvents && statsData.topEvents.execution_click >= 3) {
        insightBlock += `

⚡ HIGH-CONVERSION PATH DETECTED`;
      }

      // Add trending query signal
      if (statsData && statsData.topQueries) {
        const queryLower = query.toLowerCase();
        const isTrending = statsData.topQueries.some((topQuery: string) => 
          queryLower.includes(topQuery.toLowerCase()) || topQuery.toLowerCase().includes(queryLower)
        );
        
        if (isTrending) {
          insightBlock += `

📈 TRENDING SEARCH PATTERN`;
        }
      }

      // Append insight block to output
      enhancedOutput += insightBlock;

      setResult(enhancedOutput);
      setAiStatus('READY');

      // Navigation signal (after AI response displayed)
      let targetModule: ModuleId | null = null;
      const q = query.toLowerCase();

      // PRIORITY: Force immediate Funnels navigation for income intent
      if (
        q.includes('money') ||
        q.includes('income') ||
        q.includes('funnel') ||
        q.includes('make money') ||
        q.includes('passive')
      ) {
        targetModule = 'funnels';
      } else if (q.includes('invest') || q.includes('capital')) {
        targetModule = 'investor';
      } else if (q.includes('vault') || q.includes('assets')) {
        targetModule = 'vault';
      } else if (q.includes('ai') || q.includes('automation')) {
        targetModule = 'ai';
      }

      // Trigger navigation immediately (no delay for funnels)
      if (targetModule && onNavigate) {
        setResult(prev => prev + `

⚡ Opening income system...`);
        
        // Immediate navigation (no setTimeout delay)
        onNavigate(targetModule);
      }
    } catch (err) {
      setResult(`CAPITAL AI RESPONSE:

RECOMMENDATION:
Start with affiliate marketing using a simple funnel.

CONFIDENCE:
Medium

REASONING:
Low startup cost and fast execution.

SAFE ACTION:
Create one funnel and drive traffic using free methods.`);
      setAiStatus('READY');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (e.target.value) {
      setAiStatus('LISTENING...');
    } else {
      setAiStatus('READY');
    }
  };

  return (
    <div style={{ padding: '20px', color: 'white' }}>
      <h2 style={{ color: '#00E5FF' }}>SOVEREIGN SEARCH</h2>

      {/* AI Status Line */}
      <div style={{ 
        marginTop: '15px', 
        fontSize: '12px', 
        color: aiStatus === 'READY' ? '#00E5FF' : aiStatus === 'LISTENING...' ? '#FFD700' : '#FF6B00',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        CAPITAL AI: {aiStatus}
      </div>

      <input
        value={query}
        onChange={handleInputChange}
        placeholder="Enter command..."
        style={{
          width: '100%',
          padding: '10px',
          marginTop: '10px',
          background: 'black',
          border: '1px solid #00E5FF',
          color: '#00E5FF'
        }}
      />

      <button
        onClick={handleSearch}
        style={{
          marginTop: '10px',
          padding: '10px',
          background: 'black',
          border: '1px solid #00E5FF',
          color: '#00E5FF',
          cursor: 'pointer'
        }}
      >
        Execute
      </button>

      {result && (
        <>
          <h3 style={{ color: '#00E5FF', marginTop: '30px' }}>CAPITAL AI RESPONSE</h3>
          <div 
            style={{ 
              marginTop: '20px', 
              whiteSpace: 'pre-wrap',
            }}
            dangerouslySetInnerHTML={{
              __html: result
                .replace(/CONFIDENCE:\s*(High|Medium|Low)/gi, (match, level) => {
                  const color = level.toLowerCase() === 'high' ? '#00FF00' : 
                                level.toLowerCase() === 'medium' ? '#FFD700' : '#FF4444';
                  const glow = level.toLowerCase() === 'high' ? '0 0 10px rgba(0,255,0,0.6)' : 
                               level.toLowerCase() === 'medium' ? '0 0 10px rgba(255,215,0,0.6)' : 
                               '0 0 10px rgba(255,68,68,0.6)';
                  return `<span style="color: ${color}; text-shadow: ${glow}; font-weight: bold;">CONFIDENCE: ${level}</span>`;
                })
                .replace(/NWG EXECUTION PATH:/g, '<div style="border-top: 1px solid #00E5FF; padding-top: 10px; margin-top: 15px;"><strong>NWG EXECUTION PATH:</strong></div>')
                .replace(/\n/g, '<br />')
            }}
          />
          
          {result.includes('NWG EXECUTION PATH') && (
            <button
              onClick={async () => {
                // Track execution click
                try {
                  await fetch(`/api/track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      eventType: 'execution_click', 
                      query,
                      sessionId: getSessionId()
                    })
                  });
                } catch (e) {
                  console.log('Tracking failed:', e);
                }
                
                window.open('https://nextwealth.systeme.io/083c1601?src=nwg_cube', '_blank');
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 18px rgba(0,229,255,0.9)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                background: '#00E5FF',
                border: 'none',
                borderRadius: '4px',
                color: 'black',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'box-shadow 0.2s ease'
              }}
            >
              🚀 Launch NextWealth™ System
            </button>
          )}
        </>
      )}
    </div>
  );
}
